import { useNavigate } from "react-router-dom";
import { fmtIDR } from "@/lib/api";
import { fmtDate } from "@/components/DateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExternalLink } from "lucide-react";

const ROUTE = { invoice: "/invoice", production: "/invoice", fixed_asset: "/aset", payroll: "/payroll", fx_reval: "/laporan" };
const MENU = { invoice: "Invoice", production: "Invoice › Produksi", fixed_asset: "Aset Tetap", payroll: "Payroll", fx_reval: "Laporan (revaluasi valas otomatis)" };

// Read-only view of an automatic journal shown in the Transactions list.
export function AutoJournalDialog({ row, onClose }) {
  const nav = useNavigate();
  const lines = row?.lines || [];
  const sum = (k) => lines.reduce((s, l) => s + Number(l[k] || 0), 0);
  return (
    <Dialog open={!!row} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="auto-journal-dialog">
        {row && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">Jurnal Otomatis <Badge variant="secondary">{row.source_label}</Badge></DialogTitle>
              <DialogDescription className="text-xs">{fmtDate(row.date)}{row.invoice_no ? ` · ${row.invoice_no}` : ""}{row.bank_name ? ` · ${row.flow === "in" ? "masuk ke" : "keluar dari"} ${row.bank_name}` : ""}</DialogDescription>
            </DialogHeader>
            {row.description && <p className="text-sm">{row.description}</p>}
            <div className="overflow-auto rounded-md border border-border">
              <Table>
                <TableHeader><TableRow className="bg-secondary/50"><TableHead>Akun</TableHead><TableHead>Buku Besar</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Kredit</TableHead></TableRow></TableHeader>
                <TableBody>
                  {lines.map((l, i) => (
                    <TableRow key={i} data-testid={`auto-journal-line-${i}`}>
                      <TableCell className="text-xs"><span className="num">{l.coa_code}</span> {l.coa_name}</TableCell><TableCell className="text-xs">{l.ledger || "—"}</TableCell>
                      <TableCell className="num text-right text-xs">{l.debit ? fmtIDR(l.debit) : ""}</TableCell><TableCell className="num text-right text-xs">{l.credit ? fmtIDR(l.credit) : ""}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="font-semibold"><TableCell colSpan={2} className="text-xs">Total</TableCell>
                    <TableCell className="num text-right text-xs">{fmtIDR(sum("debit"))}</TableCell><TableCell className="num text-right text-xs">{fmtIDR(sum("credit"))}</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>
            <p className="text-[11px] text-muted-foreground">{row.source === "fx_reval"
              ? "Revaluasi valas akhir bulan dengan kurs KMK, dibuat otomatis. Sudah ikut di Buku Besar & Neraca; di laporan Bank & Setara Kas tampil sebagai Nilai KMK dan Selisih Kurs belum terealisasi."
              : `Dibuat otomatis oleh sistem dan sudah ikut di Buku Besar, laporan Bank & Setara Kas (bila menyentuh rekening bank/kas), dan akun terkait. Untuk mengubah, buka menu ${MENU[row.source] || row.source_label}.`}</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => { onClose(); nav(ROUTE[row.source] || "/jurnal"); }} data-testid="auto-journal-open-source"><ExternalLink className="h-4 w-4 mr-1" />Buka {MENU[row.source] || "Jurnal"}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
